<script lang="ts">
	import { Verbiage, t } from '@infinitro/content';
	import { LeftIcon, SearchIcon } from '@infinitro/icons';
	import type { ArticleTreeNode } from '../types/article-tree';
	import { displayListedArticleTotal } from '../utils/support-chat-article-tree';

	export let articleRoots: ArticleTreeNode[] = [];
	export let normalizedSearch = '';
	export let search = '';
	export let onOpenLeaf: (node: ArticleTreeNode) => void = () => {};

	let navigationStack: ArticleTreeNode[] = [];
	const collectionsLabel = 'Collections';
	const articlesLabel = 'articles';

	$: currentLevel = navigationStack[navigationStack.length - 1];
	$: currentNodes = currentLevel?.children ?? articleRoots;
	$: rootCollectionsCount = articleRoots.length;
	$: levelHeading = currentLevel ? currentLevel.title : $t(Verbiage.help);
	$: levelCountLabel = currentLevel
		? `${visibleNodes.length} ${articlesLabel}`
		: `${rootCollectionsCount} ${collectionsLabel}`;
	$: visibleNodes = normalizedSearch
		? currentNodes.filter((node) => {
				const title = node.title.toLowerCase();
				const subtitle = node.subtitle?.toLowerCase() ?? '';
				return `${title} ${subtitle}`.includes(normalizedSearch);
			})
		: currentNodes;

	function openNode(node: ArticleTreeNode) {
		if (node.children?.length) {
			navigationStack = [...navigationStack, node];
			return;
		}

		if (node.articleSlug?.trim()) {
			onOpenLeaf(node);
			return;
		}
	}

	function goBack() {
		if (!navigationStack.length) return;
		navigationStack = navigationStack.slice(0, -1);
	}

	/** Hub/section rows with children — show nested article counts in the primary column. */
	const isExpandableArticleGroup = (node: ArticleTreeNode): boolean =>
		Boolean(node.children?.length);
</script>

<section class="screen-help">
	<div class="screen-topbar">
		<button
			class="back-button"
			type="button"
			on:click={goBack}
			class:back-button-hidden={!navigationStack.length}
		>
			<span class="back-icon"><LeftIcon /></span>
		</button>
		<h2 class="screen-title">{levelHeading}</h2>
		<span class="screen-title-spacer"></span>
	</div>

	<div class="screen-content">
		<label class="sr-only" for="support-chat-help-search">
			{$t(Verbiage.support_chat_search_for_help)}
		</label>
		<div class="search-field">
			<input
				id="support-chat-help-search"
				class="search-field-input"
				type="text"
				bind:value={search}
				placeholder={$t(Verbiage.support_chat_search_for_help)}
			/>
			<span class="search-icon">
				<SearchIcon />
			</span>
		</div>

		<p class="collections-label">{levelCountLabel}</p>

		{#if visibleNodes.length}
			<div class="list-plain">
				{#each visibleNodes as node}
					<button class="list-row" type="button" on:click={() => openNode(node)}>
						<div class="list-copy">
							<p class="list-title">{node.title}</p>
							{#if node.subtitle}
								<p class="list-description">{node.subtitle}</p>
							{/if}
							{#if isExpandableArticleGroup(node)}
								<p class="list-link">
									{displayListedArticleTotal(node)}
									{articlesLabel}
								</p>
							{/if}
						</div>
						<span class="row-arrow" aria-hidden="true">
							<svg
								class="row-arrow-svg"
								viewBox="0 0 6 10"
								width="7"
								height="12"
								focusable="false"
								aria-hidden="true"
							>
								<path
									fill="currentColor"
									d="M3.79599 4.99956L0.0834928 8.71206L1.14399 9.77256L5.91699 4.99956L1.14399 0.226562L0.0834928 1.28706L3.79599 4.99956Z"
								/>
							</svg>
						</span>
					</button>
				{/each}
			</div>
		{:else}
			<p class="empty-state">{$t(Verbiage.support_chat_no_messages)}</p>
		{/if}
	</div>
</section>

<style>
	.screen-help {
		display: flex;
		height: 100%;
		flex-direction: column;
		background-color: var(--color-text-100);
}

	.screen-topbar {
		display: grid;
		grid-template-columns: 32px 1fr 32px;
		align-items: center;
		background-color: var(--color-text-100);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.25rem;
		padding-bottom: 0.25rem;
}

	.screen-title {
		text-align: center;
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 600;
		line-height: 1;
		color: var(--color-text-900);
}

	.screen-title-spacer {
		display: block;
		height: 2rem;
		width: 2rem;
}

	.back-button {
		display: flex;
		height: 2rem;
		width: 2rem;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		border-radius: 9999px;
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 150ms;
		color: var(--color-text-900);
		background-color: rgba(var(--color-background-900-rgb), 0.1);
}

	.back-button:not(.back-button-hidden):hover {
		background-color: rgba(var(--color-background-900-rgb), 0.16);
	}

	.back-button-hidden {
		pointer-events: none;
		opacity: 0;
}

	.back-icon {
		display: block;
		height: 1rem;
		width: 1rem;
		flex-shrink: 0;
		color: var(--color-text-900);
		filter: brightness(0.45);
}

	.screen-content {
		display: flex;
		flex: 1 1 0%;
		flex-direction: column;
		gap: 0.5rem;
		background-color: var(--color-text-100);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
}

	.search-field {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.75rem;
		border-radius: 12px;
		border-style: none;
		--tw-bg-opacity: 1;
		background-color: rgb(226 227 228 / var(--tw-bg-opacity, 1));
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.75rem;
		padding-bottom: 0.75rem;
		text-align: left;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 600;
		color: var(--color-text-900);
}

	.search-icon {
		display: block;
		height: 1rem;
		width: 1rem;
		flex-shrink: 0;
		color: var(--color-text-900);
		filter: brightness(0.6);
}

	.search-field-input {
		min-width: 0px;
		flex: 1 1 0%;
		border-style: none;
		background-color: transparent;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 600;
		color: var(--color-text-900);
		outline: 2px solid transparent;
		outline-offset: 2px;
}

	.search-field-input:-moz-placeholder {
		opacity: 0.6;
}

	.search-field-input:placeholder-shown {
		opacity: 0.6;
}

	.search-field-input:not(:-moz-placeholder) {
		opacity: 1;
}

	.search-field-input:not(:placeholder-shown) {
		opacity: 1;
}

	.search-field-input::-moz-placeholder {
		color: rgba(var(--color-text-900-rgb), 0.48);
	}

	.search-field-input::placeholder {
		color: rgba(var(--color-text-900-rgb), 0.48);
	}

	.collections-label {
		font-size: 0.75rem;
		font-weight: 600;
		line-height: 1rem;
}

	.list-plain {
		margin-left: -1rem;
		margin-right: -1rem;
}

	.list-row {
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: space-between;
		gap: 0.75rem;
		border-top-width: 1px;
		--tw-border-opacity: 1;
		border-color: rgb(209 213 219 / var(--tw-border-opacity, 1));
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.625rem;
		padding-bottom: 0.625rem;
		text-align: left;
}

	.list-copy {
		display: flex;
		min-width: 0px;
		flex-direction: column;
		gap: 0.125rem;
}

	.list-title {
		font-size: 1rem;
		line-height: 1.5rem;
		font-weight: 600;
		line-height: 1.25;
		color: var(--color-text-900);
}

	.list-description {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-size: 0.75rem;
		line-height: 1rem;
		color: var(--color-text-500);
}

	.list-link {
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 600;
		color: var(--color-secondary-500);
		text-underline-offset: 2px;
}

	.row-arrow {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: center;
		color: var(--color-secondary-500);
}

	.row-arrow-svg {
		display: block;
		height: 12px;
		width: 7px;
		flex-shrink: 0;
}

	.empty-state {
		padding-left: 0.25rem;
		padding-right: 0.25rem;
		padding-top: 1.25rem;
		padding-bottom: 1.25rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		color: var(--color-text-500);
}

	.back-icon :global(svg),
	.back-icon :global(img),
	.search-icon :global(svg),
	.search-icon :global(img) {
		height: 100%;
		width: 100%;
}

	.search-icon :global(svg) {
		color: inherit;
	}

	.search-icon :global(path),
	.search-icon :global(circle),
	.search-icon :global(rect) {
		fill: currentColor;
	}

	.back-icon :global(svg) {
		color: inherit;
	}

	.back-icon :global(path),
	.back-icon :global(circle),
	.back-icon :global(rect) {
		fill: currentColor;
	}

	.back-icon :global(path[stroke]),
	.back-icon :global(polyline),
	.back-icon :global(line) {
		stroke: currentColor;
	}

	.sr-only {
		position: absolute;
		height: 1px;
		width: 1px;
		overflow: hidden;
		white-space: nowrap;
		padding: 0px;
		clip: rect(0, 0, 0, 0);
		clip-path: inset(50%);
		margin: -1px;
		border: 0;
}
</style>
